import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { PageContainer } from "../../components/PageComponents";
import { Transable } from "./TransactionTable";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../redux/store";
import {
  fetchTransactions,
  setFilters as setTransactionFilters,
  clearFilters as clearTransactionFilters,
} from "../../features/auth/store/transactionSlice";
import {
  TransactionFilters,
  TransactionPagination,
} from "../../features/auth/store/types/transaction-types";
import { SearchIcon, Dropdown } from "../../assets/Icons";
import { TransactionDetail } from "./types";
import {
  fetchAllTransactions,
  writeTransactionsWorkbook,
  ExportProgress,
} from "./exportTransactions";

const Transactions = () => {
  const dispatch = useAppDispatch();
  const { transactions, pagination, loading, error, filters } = useAppSelector(
    (state: RootState) => state.transactions,
  );
  const token = useAppSelector((state: RootState) => state.auth.token);

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(
    null,
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.search]);

  const fetchData = useCallback(() => {
    const params: Partial<TransactionFilters> = { ...filters };

    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    } else {
      delete params.search;
    }

    dispatch(fetchTransactions(params));
  }, [dispatch, filters, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (
    key: keyof TransactionFilters,
    value: string | number,
  ) => {
    dispatch(
      setTransactionFilters({
        [key]: value,
        ...(key !== "page" ? { page: 1 } : {}),
      } as Partial<TransactionFilters>),
    );
  };

  const handleClearFilters = () => {
    dispatch(clearTransactionFilters());
  };

  /**
   * Exports every transaction matching the filters currently applied — not
   * just the page on screen — so the file is usable for reconciliation.
   */
  const handleExport = async () => {
    if (!token) {
      toast.error("Your session has expired. Please sign in again.");
      return;
    }

    setExporting(true);
    setExportProgress(null);

    try {
      const params: TransactionFilters = { ...filters };
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      } else {
        params.search = "";
      }

      const { rows, truncated, reportedTotal } = await fetchAllTransactions({
        filters: params,
        token,
        onProgress: setExportProgress,
      });

      if (rows.length === 0) {
        toast.info("No transactions match the current filters.");
        return;
      }

      await writeTransactionsWorkbook(rows, params.source);

      if (truncated) {
        toast.warn(
          `Downloaded the first ${rows.length.toLocaleString()} of ${reportedTotal.toLocaleString()} transactions. Narrow the date range to download the rest.`,
        );
      } else {
        toast.success(
          `Downloaded ${rows.length.toLocaleString()} transaction${
            rows.length === 1 ? "" : "s"
          }.`,
        );
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Could not download transactions. Please try again.",
      );
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  return (
    <PageContainer title="Transactions">
      <div className="flex flex-col gap-4">
        <TransactionSearchFilter
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          loading={loading}
          onExport={handleExport}
          exporting={exporting}
          exportProgress={exportProgress}
          hasRows={(pagination?.total ?? 0) > 0}
        />
        <div className="border border-[#FFFFFF1A] rounded-2xl">
          {error && (
            <div className="text-red-500 text-sm px-4 py-2 border-b border-[#FFFFFF1A]">
              {error}
            </div>
          )}
          <Transable
            transactions={transactions as unknown as TransactionDetail[]}
            loading={loading}
          />
          {pagination && (
            <Pagination
              pagination={pagination}
              loading={loading}
              onPageChange={(page) => handleFilterChange("page", page)}
            />
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default Transactions;

type TransactionSearchFilterProps = {
  filters: TransactionFilters;
  onFilterChange: (
    key: keyof TransactionFilters,
    value: string | number,
  ) => void;
  onClearFilters: () => void;
  loading: boolean;
  onExport: () => void;
  exporting: boolean;
  exportProgress: ExportProgress | null;
  hasRows: boolean;
};

const TransactionSearchFilter = ({
  filters,
  onFilterChange,
  onClearFilters,
  loading,
  onExport,
  exporting,
  exportProgress,
  hasRows,
}: TransactionSearchFilterProps) => {
  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "0", label: "Successful" },
    { value: "1", label: "Pending" },
    { value: "2", label: "Paid" },
    { value: "3", label: "Queued" },
    { value: "6", label: "Failed" },
    { value: "5", label: "Reversed" },
  ];

  const typeOptions = [
    { value: "", label: "All Types" },
    { value: "0", label: "Data" },
    { value: "1", label: "Airtime" },
    { value: "2", label: "Cable" },
    { value: "3", label: "Electricity" },
    { value: "4", label: "Top-up" },
    { value: "5", label: "Game Card" },
    { value: "6", label: "Gift Card" },
    { value: "7", label: "DG Coins Purchase" },
    { value: "8", label: "DG Points Reward" },
    { value: "9", label: "Transfer To User" },
    { value: "10", label: "Transfer From User" },
    { value: "11", label: "Admin Withdrawal" },
    { value: "12", label: "Admin Payment" },
    { value: "13", label: "Reversal" },
  ];

  const handleDateChange = (key: "start_date" | "end_date", value: string) => {
    onFilterChange(key, value);
  };

  return (
    /* Changed layout to flex-wrap with uniform gap-3 across all components on wide viewports */
    <div className="flex flex-wrap items-center gap-3 mb-4 w-full">
      {/* Active/Archived Toggle Buttons Block */}
      <div className="inline-flex w-full sm:w-auto rounded-3xl border border-[#D0D5DD] dark:border-[#FFFFFF1A] overflow-hidden shrink-0">
        {[
          { value: "active", label: "Active" },
          { value: "archived", label: "Archived" },
        ].map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={loading}
            onClick={() => onFilterChange("source", option.value)}
            className={`flex-1 sm:flex-initial px-4 py-2 text-xs sm:text-sm font-medium ${
              filters.source === option.value
                ? "bg-[#027EC2] text-white"
                : "bg-[#F4F4F5] text-gray-600 dark:bg-[#27272A] dark:text-white"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Search Bar Input Block */}
      <div className="relative w-full sm:w-auto flex-1 sm:flex-initial sm:min-w-[300px] xl:max-w-[400px]">
        <input
          type="text"
          value={filters.search}
          onChange={(e) => onFilterChange("search", e.target.value)}
          placeholder="Search by reference or description..."
          disabled={loading}
          className="border p-2 pl-10 rounded-3xl w-full h-[40px] sm:h-[44px] text-sm dark:text-white dark:bg-[#FFFFFF1A] bg-[#F4F4F5] dark:border-[#FFFFFF1A]"
        />
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
          <SearchIcon />
        </div>
      </div>

      {/* Date Range Inputs Block */}
      <div className="flex items-center gap-2 w-full sm:w-auto">
        <input
          type="date"
          value={filters.start_date}
          onChange={(e) => handleDateChange("start_date", e.target.value)}
          disabled={loading}
          className="border p-2 bg-[#F4F4F5] flex-1 sm:flex-initial sm:w-[135px] h-[40px] sm:h-[44px] rounded-3xl text-xs sm:text-sm disabled:opacity-50 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46]"
          placeholder="Start Date"
        />
        <input
          type="date"
          value={filters.end_date}
          onChange={(e) => handleDateChange("end_date", e.target.value)}
          disabled={loading}
          className="border p-2 bg-[#F4F4F5] flex-1 sm:flex-initial sm:w-[135px] h-[40px] sm:h-[44px] rounded-3xl text-xs sm:text-sm disabled:opacity-50 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46]"
          placeholder="End Date"
        />
      </div>

      {/* Status Selection Menu Block */}
      <div className="relative flex-1 sm:flex-initial sm:w-[150px] min-w-[110px]">
        <select
          value={filters.status}
          onChange={(e) => onFilterChange("status", e.target.value)}
          disabled={loading}
          className="border p-2 bg-[#F4F4F5] w-full h-[40px] sm:h-[44px] rounded-3xl text-xs sm:text-sm appearance-none pr-8 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46]"
        >
          {statusOptions.map((option) => (
            <option
              key={option.value}
              value={option.value}
              className="bg-white dark:bg-[#27272A] text-black dark:text-white"
            >
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <Dropdown />
        </div>
      </div>

      {/* Transaction Type Menu Block */}
      <div className="relative flex-1 sm:flex-initial sm:w-[150px] min-w-[110px]">
        <select
          value={filters.type}
          onChange={(e) => onFilterChange("type", e.target.value)}
          disabled={loading}
          className="border p-2 bg-[#F4F4F5] w-full h-[40px] sm:h-[44px] rounded-3xl text-xs sm:text-sm appearance-none pr-8 dark:bg-[#27272A] dark:text-white dark:border-[#3F3F46]"
        >
          {typeOptions.map((option) => (
            <option
              key={option.value}
              value={option.value}
              className="bg-white dark:bg-[#27272A] text-black dark:text-white"
            >
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <Dropdown />
        </div>
      </div>

      {/* Reset Filter Clear Button */}
      <button
        onClick={onClearFilters}
        disabled={loading || exporting}
        className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-5 h-[40px] sm:h-[44px] rounded-3xl text-xs sm:text-sm dark:text-white dark:bg-[#FFFFFF1A] hover:bg-gray-100 dark:hover:bg-[#FFFFFF33] disabled:opacity-50 font-medium whitespace-nowrap shrink-0 w-full sm:w-auto"
      >
        Clear
      </button>

      {/*
        Exports every row matching the filters above, not just the current
        page. On a wide date range that can be thousands of rows, so the label
        doubles as a progress readout.
      */}
      <button
        type="button"
        onClick={onExport}
        disabled={loading || exporting || !hasRows}
        title={
          hasRows
            ? "Download all transactions matching the current filters as an Excel file"
            : "No transactions to download"
        }
        className="bg-[#027EC2] text-white px-5 h-[40px] sm:h-[44px] rounded-3xl text-xs sm:text-sm hover:bg-[#0269a3] disabled:opacity-50 disabled:cursor-not-allowed font-medium whitespace-nowrap shrink-0 w-full sm:w-auto"
      >
        {exporting
          ? exportProgress && exportProgress.total > 0
            ? `Downloading ${exportProgress.fetched.toLocaleString()} / ${exportProgress.total.toLocaleString()}...`
            : "Downloading..."
          : "Download"}
      </button>
    </div>
  );
};

type PaginationProps = {
  pagination: TransactionPagination;
  loading: boolean;
  onPageChange: (page: number) => void;
};

const Pagination = ({ pagination, loading, onPageChange }: PaginationProps) => {
  const { current_page, last_page, from, to, total } = pagination;

  const handlePrevious = () => {
    if (current_page > 1) {
      onPageChange(current_page - 1);
    }
  };

  const handleNext = () => {
    if (current_page < last_page) {
      onPageChange(current_page + 1);
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:justify-between md:items-center border-t border-[#FFFFFF1A] p-4 gap-2">
      <div className="flex gap-2">
        <button
          className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs sm:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-[#FFFFFF33]"
          onClick={handlePrevious}
          disabled={current_page <= 1 || loading}
        >
          Previous
        </button>
        <button
          className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs sm:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-[#FFFFFF33]"
          onClick={handleNext}
          disabled={current_page >= last_page || loading}
        >
          Next
        </button>
      </div>
      <span className="text-gray-600 dark:text-white text-xs sm:text-sm">
        Showing {from || 0} to {to || 0} of {total || 0} results
        {last_page > 1 && ` (Page ${current_page} of ${last_page})`}
      </span>
    </div>
  );
};
